import { specialisationFaqs } from "@/data/specialisationFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { fitCallUrl } from "@/lib/booking";
import { m } from "framer-motion";
import { ArrowRight, HardHat } from "lucide-react";
import {
  canonicalLinks,
  ogUrl,
  jsonLd,
  serviceSchema,
  breadcrumbSchema,
  faqPageSchema,
  webPageSchema, absoluteUrl, ogImage } from "@/lib/seo";
import { PhotoBand } from "@/components/site/PhotoBand";
import tradespersonVehicleImg from "@/assets/photos/tradesperson-vehicle.webp";
import { NextStep } from "@/components/site/NextStep";
import { DeliveredThrough } from "@/components/site/CrossLinks";

const PATH = "/specialisations/project-and-commercial-control";
const TITLE =
  "Project and contract commercial control for SMEs";
const DESCRIPTION =
  "Cost-to-complete visibility, labour cost reporting, progress claims discipline, variation capture and margin-by-job reporting for project-based operators.";

const CAREER_NOTE =
  "These results come from Adam's operating career before Ajility, inside the businesses themselves.";

const proof = [
  {
    figure:
      "Commercial control disciplines built in senior operating roles across mining services, transport and electrical contracting",
    org: "Senior operating roles",
  },
  {
    figure:
      "Enterprise agreement financial modelling delivering a $1.2M three-year benefit",
    org: "A 249-store national retailer",
  },
];

const covers = [
  {
    title: "Cost-to-complete visibility",
    desc: "A current view of what remains to be spent on every live job, rather than a record of what has already been spent on it.",
  },
  {
    title: "Labour cost reporting",
    desc: "Hours booked against jobs at true cost, including on-costs, travel and non-productive time, reported weekly rather than at month end.",
  },
  {
    title: "Progress claims discipline",
    desc: "Claims raised on schedule, supported by evidence, tracked to certification and payment, with someone accountable for each date.",
  },
  {
    title: "Variation capture",
    desc: "Scope changes recognised when they happen, priced, submitted and approved before the work is delivered, not argued about afterwards.",
  },
  {
    title: "Margin by job",
    desc: "Quoted margin, current forecast margin and final margin on one page, so a job going backwards is visible while it can still be changed.",
  },
  {
    title: "The weekly commercial meeting",
    desc: "One short, fixed cadence where every live job is reviewed against those numbers, with named owners and dates on the actions.",
  },
];

const faqs = specialisationFaqs["project-and-commercial-control"];

export const Route = createFileRoute("/specialisations/project-and-commercial-control")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("specialisations-project-and-commercial-control", `${TITLE} by Ajility`),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(webPageSchema({ mainEntityId: `${absoluteUrl(PATH)}#service`, path: PATH, name: `${TITLE} | Ajility`, description: DESCRIPTION })),
      jsonLd(
        breadcrumbSchema([
          { name: "Expertise", path: "/specialisations" },
          { name: "Project and commercial control", path: PATH },
        ]),
      ),
      jsonLd(
        serviceSchema({
          name: "Project and contract commercial control",
          description: DESCRIPTION,
          path: PATH,
          offerId: "install-performance-commercial-discipline-install",
          slug: "project-and-commercial-control",
          serviceType: "Project and contract commercial control",
        }),
      ),
      jsonLd(faqPageSchema(PATH, faqs, `${absoluteUrl(PATH)}#service`)),
    ],
  }),
  component: ProjectControlSpecialisation,
});

function ProjectControlSpecialisation() {
  return (
    <>
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-24 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <m.div
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative mx-auto max-w-7xl px-5 sm:px-8"
        >
          <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/[0.04] text-steel-on-dark">
            <HardHat className="h-6 w-6" aria-hidden="true" />
          </div>
          <span className="eyebrow mt-8 block on-dark">Expertise: project and commercial control</span>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl text-balance">
            The project made money in the quote and lost it in the delivery.
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-frost/75 text-pretty">
            For a subcontractor the processes that matter most are estimating, job costing, variations and progress claims. Ajility installs cost to complete, weekly labour cost reporting and a variation process; it does not do quantity surveying or legal claims work. 
            For contractors, trades businesses, fit-out, electrical and construction-adjacent
            operators, <Link to="/insights/$slug" params={{ slug: "electrical-margins" }} className="underline decoration-white/40 underline-offset-4 hover:decoration-frost">margin is decided after the job is won</Link>. This is the operational commercial
            control that makes the position on every live job visible while it can still be
            changed. It is not quantity surveying and it is not legal claims work.
          </p>
          <div className="mt-10 flex flex-wrap items-start gap-4">
            <BookFitCall
              placement="specialisations_project_commercial_hero"
              buttonClassName="group inline-flex items-center gap-2 rounded-full bg-frost px-6 py-3 text-sm font-semibold text-midnight-deep transition hover:bg-white"
              noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
            >
              Book a fit call
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
            </BookFitCall>
            <Link
              to="/packages"
              className="inline-flex rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-frost/85 transition hover:border-white/30 hover:text-frost"
            >
              See the Performance & Commercial Discipline install
            </Link>
          </div>
        </m.div>
      </section>

      <PhotoBand
        src={tradespersonVehicleImg}
        alt="A tradesperson taking tools from the drawers at the rear of a service van"
        priority
      />

      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow">What the work covers</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            Six controls that decide the margin on a job
          </h2>
          <p className="mt-4 max-w-3xl text-charcoal">
            Most project businesses know their annual result and very little in between. These six
            controls turn a quoted margin into a tracked one, reviewed weekly by the people who can
            still do something about it.
          </p>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {covers.map((c, i) => (
              <m.div
                key={c.title}
                initial={{ y: 16 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-panel"
              >
                <h3 className="font-serif text-xl text-foreground">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal">{c.desc}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-surface py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">Where the margin goes</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">
            The quote is an estimate. The delivery is the result.
          </h2>
          <p className="mt-6 leading-relaxed text-charcoal">
            A job usually loses money in small, ordinary ways: hours booked to the wrong job or not
            booked at all, a variation delivered on a handshake, a claim submitted a week after the
            cut-off, an allowance consumed early with nobody re-forecasting the rest. None of these
            is visible in a monthly P&L, which reports the average of good jobs and bad ones.
          </p>
          <p className="mt-4 leading-relaxed text-charcoal">
            Commercial control is the weekly habit that makes each job visible on its own terms:
            what it was quoted at, what it will cost to finish, what has been claimed and what has
            been approved. The reporting is deliberately plain, because it has to be produced every
            week by the operations team without help.
          </p>
        </div>
      </section>

      <section className="bg-midnight py-24 text-frost">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow on-dark">Track record</span>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-balance">
            Commercial control built in senior operating roles
          </h2>
          <p className="mt-4 max-w-3xl text-steel-on-dark">{CAREER_NOTE}</p>
          <ul className="mt-12 grid gap-6 md:grid-cols-2">
            {proof.map((p) => (
              <li key={p.figure} className="rounded-2xl border border-white/10 bg-white/[0.04] p-7">
                <p className="font-serif text-lg leading-snug text-frost">{p.figure}</p>
                <p className="mt-3 text-sm text-steel-on-dark">{p.org}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-background py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">Tenders and re-tenders</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground">
            Winning the bid without buying the work
          </h2>
          <p className="mt-4 text-charcoal">
            Commercial control starts before the job does. A bid priced off last year&apos;s rates,
            or off a cost base nobody has tested against actual delivery, produces a job that was
            unprofitable the day it was signed and a team blamed for it eighteen months later. The
            discipline is to price the bid from the real cost to complete, name the assumptions that
            the price depends on, and carry those assumptions into the job so variations can be
            claimed against them. At re-tender, the same job history is the argument: what the
            account earned, where it went wrong, what has been fixed, and which terms have to move
            for the work to be worth keeping.
          </p>
        </div>
      </section>

      <section className="bg-mist py-20">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <span className="eyebrow">Where this is bought</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground">
            Performance & Commercial Discipline install
          </h2>
          <p className="mt-4 text-charcoal">
            This work is installed through the Performance & Commercial Discipline service line, or
            scoped first through the Operating Diagnostic where it is not yet clear that commercial
            control is the binding constraint. Both are published, with their scope and fee, on the
            packages page.
          </p>
          <p className="mt-4 text-charcoal">Read <Link to="/insights/$slug" params={{ slug: "security-of-payment-act-subcontractors" }} className="underline underline-offset-4">Security of Payment Act for subcontractors: getting paid when a builder slows down</Link> for the practical questions behind this work.</p>
          <Link
            to="/packages"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-midnight px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-midnight-deep"
          >
            See the install and the Operating Diagnostic
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="bg-background py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">FAQ</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">Common questions</h2>
          <dl className="mt-10 space-y-6">
            {faqs.map((f) => (
              <div key={f.q} className="card-panel">
                <dt>
                  <h3 className="font-serif text-lg text-foreground">{f.q}</h3>
                </dt>
                <dd className="mt-3 text-sm leading-relaxed text-charcoal">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <DeliveredThrough slug="project-and-commercial-control" />

      <NextStep
        heading="Start with the free Operating Scan"
        body="21 plain statements about how the business actually runs, answered from Not us to Always. In about six minutes you see a provisional score naming the weakest areas, then choose whether you want a conversation."
        primaryLabel="Take the free Operating Scan"
        primaryTo="/scan"
        secondaryLabel="Book a fit call"
        secondaryTo={fitCallUrl("specialisations_project_commercial_footer")}
        secondaryNote={
          <Link to="/contact" className="text-sm font-medium text-frost/70 hover:text-frost">
            Or send a note instead
          </Link>
        }
      />
    </>
  );
}
